import { useAuth } from "@Auth/useAuth";
import { Footer } from "@common/SimpleDrawer/components/Footer";
import { Section } from "@common/SimpleDrawer/components/Section";
import { FormAutocomplete } from "@components/FormAutocomplete/FormAutoComplete";
import { FormInput } from "@components/FormInput/FormInput";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { FORM_ACTIONS } from "@models/FormActions";
import { Box } from "@mui/material";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { useDrawer } from "@hooks/useDrawer";
import { onInfoShow } from "@utils/onInfoShow";
import { useLocations } from "@features/locations/queries/useLocations";
import {
  useCreateLocation,
  useUpdateLocation,
} from "@features/locations/queries/useLocationMutations";
import { buildParentOptions } from "@features/locations/utils/parentOptions";
import { useEffect, useMemo, useReducer } from "react";

type LocationInitialState = {
  name: string;
  parent_id: string | null;
  errors: Record<keyof LocationInitialState, string>;
};

const initialState: LocationInitialState = {
  name: "",
  parent_id: null,
  errors: {} as Record<keyof LocationInitialState, string>,
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: string | null;
  errors?: Record<keyof T, string>;
}

function formReducer(
  state: LocationInitialState,
  action: Action<LocationInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD: {
      if (!action.field) return state;
      return {
        ...state,
        [action.field!]: action.value,
      };
    }
    case FORM_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: action.errors || {},
      };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

type LocationFormProps = {
  handleClose: () => void;
  createFromStockView?: boolean;
};

export const LocationForm = ({
  handleClose,
  createFromStockView,
}: LocationFormProps) => {
  const {
    context,
    selectedId,
    prefillLocationId,
    close: closeOnSubmit,
  } = useDrawer();

  const { baseUser } = useAuth();
  const { data: locations = [] } = useLocations();
  const createLocationMutation = useCreateLocation();
  const updateLocationMutation = useUpdateLocation();

  const { setTouch } = useConfirmationModalStore();

  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<LocationInitialState>,
      Action<LocationInitialState>
    >,
    initialState
  );

  useEffect(() => {
    if (context === DRAWER_CONTEXT.EDIT_LOCATION && selectedId) {
      const location = locations.find((location) => location.id === selectedId);
      if (location) {
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "name",
          value: location.name,
        });
        // Prefill the current parent so editing doesn't silently move the
        // location to root on save (the field was previously left blank).
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "parent_id",
          value: location.parent_id,
        });
      }
    } else if (context === DRAWER_CONTEXT.CREATE_LOCATION && prefillLocationId) {
      // Opened from inside a folder ("New location" / "Add sub-location") —
      // default the parent to that folder.
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "parent_id",
        value: prefillLocationId,
      });
    }
  }, [context, selectedId, prefillLocationId, locations]);

  // Exclude the location itself and its descendants when editing, so a folder
  // can't be moved into its own subtree (which would orphan a cycle).
  const parentOptions = useMemo(
    () =>
      buildParentOptions(
        locations,
        context === DRAWER_CONTEXT.EDIT_LOCATION
          ? selectedId ?? undefined
          : undefined
      ),
    [locations, context, selectedId]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: e.target.name as keyof LocationInitialState,
      value: e.target.value,
    });
    setTouch(true);
  };

  const handleFormVerification = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const errors = {} as Record<keyof LocationInitialState, string>;

    const requiredFields: (keyof LocationInitialState)[] = ["name"];

    requiredFields.forEach((field) => {
      if (!state[field]) errors[field] = `${field} is required`;
    });

    if (Object.keys(errors).length > 0) {
      dispatch({ type: FORM_ACTIONS.SET_ERRORS, errors });
      return;
    }
    return true;
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const validForm = handleFormVerification(e);

    if (!validForm) return;

    const { name } = state;

    try {
      if (context === DRAWER_CONTEXT.EDIT_LOCATION && selectedId) {
        await updateLocationMutation.mutateAsync({
          id: selectedId,
          name: name!,
          organization_id: baseUser!.organization_id!,
          parent_id: state.parent_id || null,
        });
        onInfoShow("Location updated successfully");
      } else {
        await createLocationMutation.mutateAsync({
          name: name!,
          organization_id: baseUser!.organization_id!,
          parent_id: state.parent_id || null,
        });
        onInfoShow("Location created successfully");
      }
      closeOnSubmit();
    } catch {
      // The global mutation handler maps the error (e.g. duplicate name) and
      // shows the toast; we just keep the drawer open so the user can retry.
    }
  };

  const handleCreateFromStockWindow = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const validForm = handleFormVerification(e);

    if (!validForm) return;

    const { name } = state;

    try {
      await createLocationMutation.mutateAsync({
        name: name!,
        organization_id: baseUser!.organization_id!,
        parent_id: state.parent_id || null,
      });
      onInfoShow("Location created successfully");
      handleClose();
      setTouch(false);
    } catch {
      // Global mutation handler surfaces the error toast.
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormInput
            name="name"
            label="Name"
            value={state.name}
            onChange={handleChange}
            errorMessage={state.errors?.name}
          />
          <FormAutocomplete
            label="Parent Location"
            value={state.parent_id || ""}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "parent_id" as keyof LocationInitialState,
                value: value,
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select a location..."
            errorMessage={state.errors?.parent_id}
            options={parentOptions}
          />
        </Section>
      </Box>

      <Footer
        handleClose={handleClose}
        handleSubmit={async (e) => {
          createFromStockView
            ? handleCreateFromStockWindow(e)
            : handleSubmit(e);
        }}
      />
    </Box>
  );
};
